import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchProcessos, type Processo } from '@/api/client';
import { Combobox, type ComboboxOption } from '@/components/ui/combobox';
import { useSmartSearch } from '@/hooks/useSmartSearch';
import { formatPartesResumo } from '@/lib/partes/resumo';
import { queryKeys } from '@/lib/pwa/queryConfig';
import { queryPersistMeta } from '@/lib/pwa/queryPersistence';
import { ACERVO_SEARCH_FIELDS } from '@/pages/processos/acervoSearch';

/** O acervo pode ter milhares de linhas: a lista mostra só as primeiras da busca. */
const RESULTADOS = 30;

interface ProcessoDoAcervoComboboxProps {
  id?: string;
  value: string | null;
  onChange: (processoId: string | null) => void;
  /** O número do processo já vinculado, para o rótulo antes de o acervo carregar. */
  numeroAtual?: string | null;
}

type Opcao = ComboboxOption & { processo?: Processo };

const subtitulo = (p: Processo) =>
  [formatPartesResumo(p.partes_resumo), p.classe, p.tribunal].filter(Boolean).join(' · ');

/**
 * Escolhe um processo do acervo. O acervo vem da lista do Acervo (o mesmo cache,
 * offline inclusive) e a busca é a dele, no navegador: número, partes, classe e
 * tribunal.
 */
export function ProcessoDoAcervoCombobox({ id, value, onChange, numeroAtual }: ProcessoDoAcervoComboboxProps) {
  const [busca, setBusca] = useState('');
  const { data: processos, isLoading, fetchStatus } = useQuery({
    queryKey: [...queryKeys.processos.all],
    queryFn: () => fetchProcessos(),
    meta: queryPersistMeta.nonEmpty,
  });
  const acervo = useMemo(() => processos ?? [], [processos]);
  const resultado = useSmartSearch(acervo, busca, ACERVO_SEARCH_FIELDS);

  const options = useMemo<Opcao[]>(() => {
    const paraOpcao = (p: Processo): Opcao => ({ value: p.id, label: p.numero, processo: p });
    const lista = resultado.results.slice(0, RESULTADOS).map(paraOpcao);
    // O escolhido fica sempre na lista: é dela que sai o rótulo do botão.
    if (value && !lista.some((o) => o.value === value)) {
      const atual = acervo.find((p) => p.id === value);
      if (atual) lista.unshift(paraOpcao(atual));
      else if (numeroAtual) lista.unshift({ value, label: numeroAtual });
    }
    if (value) lista.unshift({ value: '', label: 'Sem processo' });
    return lista;
  }, [resultado.results, acervo, value, numeroAtual]);

  const semConexao = fetchStatus === 'paused' && !processos;

  return (
    <Combobox
      id={id}
      options={options}
      value={value ?? ''}
      onValueChange={(v) => onChange(v || null)}
      placeholder="Nenhum (opcional)"
      searchPlaceholder="Número, parte, classe ou tribunal"
      emptyMessage={
        semConexao
          ? 'Sem conexão: abra o Acervo com a rede para escolher o processo.'
          : 'Nenhum processo encontrado no acervo.'
      }
      shouldFilter={false}
      searchValue={busca}
      onSearchChange={setBusca}
      loading={isLoading && !semConexao}
      renderOption={(opt) => {
        const p = (opt as Opcao).processo;
        if (!p) {
          return <span className={opt.value ? 'font-mono text-sm' : 'text-muted-foreground'}>{opt.label}</span>;
        }
        const linha2 = subtitulo(p);
        // Nada de `truncate` (nowrap): no modal a lista está no fluxo de um grid, e a
        // linha longa alarga a coluna — o diálogo inteiro ganhava rolagem horizontal.
        return (
          <div className="min-w-0 flex-1">
            <p className="font-mono text-sm">{p.numero}</p>
            {linha2 && <p className="line-clamp-2 text-xs [overflow-wrap:anywhere] text-muted-foreground">{linha2}</p>}
          </div>
        );
      }}
    />
  );
}
